import ErrorBoundaryView from '@/ui/components/error-boundary-view'
import { AnimatePresence } from 'framer-motion'
import { MDRMainTitle } from '@routes/mdr/routes/mdr.route.styles'
import { TabsContainer } from '@/ui/components/tabs-routing/index.styles'
import { Outlet } from 'react-router-dom'
import { ScheduleCall } from './overview/components/ScheduleCall'
import ShieldIcon from '@assets/images/mdr-shield.svg?react'
import { DemoModalTitle, TimeRangeContainer } from './overview/overview.styles'
import { SimpleMenu } from '@/ui/components/SimpleMenu'
import { DemoAccountBanner } from './overview/components/DemoAccountBanner'
import {
    DemoOverlay,
    DemoModal,
    fadeAnimation,
    slideAnimation,
    TimeRangeActivator,
    LoadingMoreIndicator,
    Spinner,
} from './mdr.route.styles'
import Typography from '@/ui/components/typography'
import { ActionButton } from '@/ui/components/buttons/action'
import { StyledArrowDown } from '@/ui/components/account-picker/index.styles'
import { useMdrRoute } from './hooks'
import TabsRouting from '@/ui/components/tabs-routing'
import { ROUTES } from '@/core/config/routing'

const MdrRoute = () => {
    const {
        isMdrActive,
        showDemo,
        isLoading,
        isLoadingMore,
        isError,
        timeRangeLabel,
        timeRangeItems,
        timeRangeMenuOpen,
        onTimeRangeActivate,
        onTimeRangeClose,
        handleTakeALook,
    } = useMdrRoute()

    const tabList: TTabsList = [
        {
            id: 'mdr-tab-overview',
            tabTitle: ROUTES.mdr.children.overview.displayName,
            path: ROUTES.mdr.children.overview.path,
            analyticsEvent: 'SEM::mdr_tab_clicked',
            analyticsParams: { tab: 'overview', isMdrActive },
        },
        {
            id: 'mdr-tab-incidents',
            tabTitle: ROUTES.mdr.children.incidents.displayName,
            path: ROUTES.mdr.children.incidents.path,
            analyticsEvent: 'SEM::mdr_tab_clicked',
            analyticsParams: { tab: 'incidents', isMdrActive },
        },
        {
            id: 'mdr-tab-alerts',
            tabTitle: ROUTES.mdr.children.alerts.displayName,
            path: ROUTES.mdr.children.alerts.path,
            analyticsEvent: 'SEM::mdr_tab_clicked',
            analyticsParams: { tab: 'alerts', isMdrActive },
        },
        {
            id: 'mdr-tab-service-requests',
            tabTitle: ROUTES.mdr.children.service_requests.displayName,
            path: ROUTES.mdr.children.service_requests.path,
            analyticsEvent: 'SEM::mdr_tab_clicked',
            analyticsParams: { tab: 'service-requests', isMdrActive },
        },
    ]

    return (
        <>
            {!isMdrActive && <ScheduleCall />}
            <div style={{ position: 'relative' }}>
                <AnimatePresence>
                    {!showDemo && !isMdrActive && (
                        <DemoOverlay {...fadeAnimation}>
                            <DemoModal {...slideAnimation}>
                                <DemoModalTitle variant="m-regular">
                                    Turn on demo mode to explore the At-Bay MDR
                                    Portal.
                                </DemoModalTitle>
                                <ActionButton
                                    text="Take a Look"
                                    data-testid="mdr-demo-button"
                                    onClick={handleTakeALook}
                                />
                            </DemoModal>
                        </DemoOverlay>
                    )}
                </AnimatePresence>
                {showDemo && !isMdrActive && <DemoAccountBanner />}
                <MDRMainTitle>
                    <ShieldIcon />
                    <Typography variant="xl-regular">
                        At-Bay Stance Managed Detection and Response
                    </Typography>
                    {isLoadingMore && (
                        <LoadingMoreIndicator>
                            <Spinner />
                            <Typography variant="s-regular">
                                Loading more data...
                            </Typography>
                        </LoadingMoreIndicator>
                    )}
                </MDRMainTitle>
                <TimeRangeContainer>
                    <Typography variant="m-regular">
                        See Overview from the
                    </Typography>
                    <SimpleMenu
                        open={timeRangeMenuOpen}
                        onActivate={onTimeRangeActivate}
                        onClose={onTimeRangeClose}
                        activator={
                            <TimeRangeActivator>
                                <Typography variant="m-regular">
                                    {timeRangeLabel}
                                </Typography>
                                {<StyledArrowDown />}
                            </TimeRangeActivator>
                        }
                        items={timeRangeItems}
                        isActivatorEnabled={!isLoading && !isLoadingMore}
                        duration={0}
                    />
                </TimeRangeContainer>
                <TabsContainer>
                    <TabsRouting tabList={tabList} tabsName="mdr-tab" />
                </TabsContainer>
                {isError ? <ErrorBoundaryView level="MDR" /> : <Outlet />}
            </div>
        </>
    )
}

export default MdrRoute
